/*
  Shared parts of Figma "Text field" and "Password field": label above, the
  field, a hint below. Field states: hover lays label/primary over it at 8%,
  focus draws a 2px accent/text outline, error a 1.5px status/danger outline,
  success a 1.5px accent/default outline. Outlines sit inside the field (inset
  shadows), so the content never moves.
*/

const RINGS = {
  default:
    "shadow-edge focus-within:shadow-[inset_0_0_0_2px_var(--color-accent-text),inset_0_1px_0_var(--color-edge-highlight)]",
  error: "shadow-[inset_0_0_0_1.5px_var(--color-status-danger),inset_0_1px_0_var(--color-edge-highlight)]",
  success: "shadow-[inset_0_0_0_1.5px_var(--color-accent-default),inset_0_1px_0_var(--color-edge-highlight)]",
};

const HINT_COLORS = {
  default: "text-label-secondary",
  error: "text-status-danger",
  success: "text-status-success",
};

/** Text field height: 48 on phones, 44 from 600px (Figma Size=Phone | Desktop). */
export const TEXT_FIELD_HEIGHT = "h-[48px] min-[600px]:h-[44px]";

export const fieldClass = ({ tone = "default", disabled = false, height = TEXT_FIELD_HEIGHT, className = "" }) =>
  [
    "relative flex w-full items-center rounded-radius-md",
    height,
    disabled ? "bg-fill-quaternary" : "bg-fill-tertiary",
    "after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:bg-label-primary after:opacity-0 after:transition-opacity",
    disabled ? "" : "hover:after:opacity-[var(--state-hover)]",
    RINGS[tone],
    className,
  ].join(" ");

/** The <input> inside a field: Callout text, placeholder in label/tertiary. */
export const INPUT_CLASS =
  "h-full min-w-0 flex-1 bg-transparent text-callout text-label-primary outline-none placeholder:text-label-tertiary disabled:cursor-not-allowed disabled:text-label-tertiary autofill:[-webkit-text-fill-color:var(--color-label-primary)] autofill:[transition:background-color_9999s]";

export const FieldLabel = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="text-footnote-semibold text-label-secondary">
    {children}
  </label>
);

export const FieldHint = ({ id, tone = "default", children }) => (
  <p id={id} className={`text-footnote ${HINT_COLORS[tone]}`}>
    {children}
  </p>
);

export const fieldTone = ({ error, success }) => (error ? "error" : success ? "success" : "default");
